import { useState, useEffect, useCallback } from "react";
import { reviewsApi, type Review } from "@/api/social";

function StarRating({ rating, onChange }: { rating: number; onChange?: (r: number) => void }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => onChange?.(star)}
          disabled={!onChange}
          className={`text-lg ${star <= rating ? "text-yellow-400" : "text-zinc-600"} ${onChange ? "hover:text-yellow-300" : "cursor-default"}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export function ReviewsSection({ companyId }: { companyId: string }) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [avgRating, setAvgRating] = useState<number | undefined>();
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newRating, setNewRating] = useState(0);
  const [newText, setNewText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reviewsApi.list(companyId);
      setReviews(res.reviews || []);
      setAvgRating(res.averageRating);
      setTotal(res.totalReviews || (res.reviews || []).length);
    } catch (e) {
      console.error("[Reviews] load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (newRating === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      await reviewsApi.submit(companyId, {
        rating: newRating,
        text: newText.trim() || undefined,
      });
      setNewRating(0);
      setNewText("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-semibold text-zinc-200">Reviews</h3>
        {avgRating !== undefined && (
          <div className="flex items-center gap-1">
            <span className="text-yellow-400">★</span>
            <span className="text-sm font-medium text-zinc-300">{avgRating.toFixed(1)}</span>
            <span className="text-xs text-zinc-500">({total})</span>
          </div>
        )}
        {loading && <span className="text-xs text-zinc-500">Loading...</span>}
      </div>

      {/* Submit review */}
      <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-400">Your rating:</span>
          <StarRating rating={newRating} onChange={setNewRating} />
        </div>
        <textarea
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Write a review..."
          rows={3}
          className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 placeholder-zinc-500 focus:border-orange-500 focus:outline-none"
        />
        {error && <p className="text-xs text-red-400">{error}</p>}
        <button
          onClick={submit}
          disabled={newRating === 0 || submitting}
          className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit Review"}
        </button>
      </div>

      {/* Reviews list */}
      {reviews.length > 0 ? (
        <div className="space-y-3">
          {reviews.map((review) => (
            <div key={review.id} className="rounded-lg border border-zinc-800 bg-zinc-900/30 p-3">
              <div className="flex items-center justify-between">
                <StarRating rating={review.rating} />
                <span className="text-xs text-zinc-500">{formatDate(review.createdAt)}</span>
              </div>
              {review.text && <p className="mt-2 text-sm text-zinc-300">{review.text}</p>}
              {review.photoUrls && review.photoUrls.length > 0 && (
                <div className="mt-2 flex gap-2">
                  {review.photoUrls.map((url, i) => (
                    <img key={i} src={url} alt="" className="h-16 w-16 rounded object-cover" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        !loading && <p className="text-sm text-zinc-500">No reviews yet</p>
      )}
    </div>
  );
}
